import React, {
  useEffect, useCallback, useRef, useState,
} from 'react';
import { useForm, FieldValues } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { IoMdAdd } from 'react-icons/io';
import styles from './client-form.module.scss';
import { IClient } from '~/types/clients';
import {
  InputText,
  ErrorField,
  // InputEmail,
  // InputFormatPattern,
} from '~/lib/HooksFormFields';
import { useUpdateClient } from '~/hooks/clients';
import Modal from '~/lib/Modal';
import ContactModal from '~/components/ContactModal';

const toClientFormValues = (client: IClient) => ({
  address: client.address,
  siret: client.siret,
  contacts: client.contacts,
});

const ClientForm = ({ client } : { client: IClient }) => {
  const {
    mutate: updateClient,
    // isLoading: isPutLoading,
  } = useUpdateClient('Les modifications ont été prise en compte');
  const [selectedContact, setSelectedContact] = useState(null);

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: toClientFormValues(client),
  });

  const modalContact = useRef<any>(null);

  const contacts = watch('contacts') || [];

  const submit = useCallback(async (data: FieldValues) => {
    await updateClient({
      _id: client._id,
      address: data.address,
      siret: data.siret,
      contacts: data.contacts,
    });
  }, [client._id, updateClient]);

  const debouncedSubmit = useDebounceCallback(submit, 1500);

  // function to watch change on all field
  const submitOnChange = () => watch(() => {
    handleSubmit(debouncedSubmit)();
  });

  const handleChangeContacts = (contact: any) => {
    const updatedContacts = [...contacts];

    if (contact?._id) {
      const contactIndex = updatedContacts.findIndex((c:any) => c._id === contact._id);
      if (contactIndex !== -1) {
        updatedContacts[contactIndex] = contact;
      } else {
        updatedContacts.push(contact);
      }
    } else {
      const existingContactIndex = updatedContacts.findIndex((c:any) => !c._id
        && c.firstName === contact.firstName
        && c.lastName === contact.lastName
        && c.email === contact.email
        && c.phone === contact.phone);

      if (existingContactIndex !== -1) {
        // Remplacer le contact existant sans _id par le nouveau
        updatedContacts[existingContactIndex] = contact;
      } else {
        // Ajouter le nouveau contact
        updatedContacts.push(contact);
      }
    }

    setSelectedContact(null);
    setValue('contacts', updatedContacts);
    return modalContact.current.close();
  };

  // Mettre à jour le formulaire quand le client change (après une mise à jour du serveur)
  useEffect(() => {
    if (client) {
      reset(toClientFormValues(client));
    }
  }, [client, reset]);

  // side effect on debouncedSubmit to trigger submit on change for existing user
  useEffect(() => {
    if (!debouncedSubmit) return () => {};
    // const subscription = watch((data) => {
    const subscription = submitOnChange();
    return () => subscription.unsubscribe();
  }, [debouncedSubmit, submitOnChange, client]);

  return (
    <>
    <div className={styles.container}>
      <div className={styles.form}>
        <section>
          <h4>Informations</h4>
          <div className={styles.containerField}>
            <InputText
              label="Adresse"
              name='address'
              control={control}
            />
          </div>
          <div className={styles.containerField}>
            <InputText
              label="SIRET"
              name='siret'
              control={control}
              rules={{
                required: 'le SIRET est requis',
                pattern: {
                  value: /^\d{14}$/,
                  message: 'le SIRET doit contenir 14 chiffres',
                },
              }}
            />
            {errors?.siret?.message && <ErrorField message={errors?.siret?.message} />}
          </div>
        </section>
        <section>
          <div className={styles.title}>
            <h4>Contacts</h4>
            <button onClick={() => modalContact.current.open()}>
              <span className='icon'>{IoMdAdd({})}</span>
              <span>Créer un contact</span>
            </button>
          </div>
          <div className={styles.contacts}>
            {contacts?.map((c:any, index: number) => <button
              type='button'
              key={c._id || `temp-${index}-${c.firstName}-${c.lastName}`}
              className={styles.contact}
              onClick={async () => {
                await setSelectedContact(c);
                await modalContact.current.open();
              }}
              >
                <h3>{c.firstName} {c.lastName}</h3>
                <p>{c.phone}</p>
                <p>{c.email}</p>

            </button>)}

          </div>
        </section>
      </div>
    </div>
    <Modal width={450} ref={modalContact}
      handleClose={() => setSelectedContact(null)}
    >
      <ContactModal
        onSubmit={handleChangeContacts}
        defaultValues={selectedContact}
      />
    </Modal>
  </>
  );
};

export default ClientForm;
